import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENVIRONMENT_URL, RAJPURA_TABLES, getRajpuraTourId } from '../../../constants/dataverse';
import { getAccessToken } from '../../../Services/getAccessToken';
import moment from 'moment';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../constants/sharepoint';
import { alcService } from '../ALC/services/alcService';
import { foodSafetyService } from '../FoodSafety/services/foodSafetyService';
import { ccpOprpSievesService } from '../CCP_OPRP_Sieves/services/ccpOprpSievesService';
import { mixingAndBakingService } from '../MixingAndBaking/services/mixingAndBakingService';
import { useSelector } from 'react-redux';

export type DashboardCategory = 'ALC' | 'FoodSafety' | 'CCP_OPRP_Sieves' | 'MixingAndBaking' | 'PackagingOperations';

export interface TourHistory {
  id: string;
  date: string;
  form: string;
  line: string;
  shift: string;
  execs: string;
  score: string | React.ReactNode;
  isLineClearBadge?: React.ReactNode;
  cycleBadge?: React.ReactNode;
  freqBadge?: React.ReactNode;
  result: string;
  status: string;
  pendingWith: string;
  isMyTask: boolean;
  isEscalatedForMe: boolean;
  isClickable: boolean;
  rawTour: any;
}

export interface DashboardMetrics {
  totalTours: number;
  metric2Label: string;
  metric2Value: number;
  metric2Color: string;
  metric3Label: string;
  metric3Value: number;
  metric3Color: string;
  metric4Label: string;
  metric4Value: number;
  metric4Color: string;
}

const RajpuraQualityDashboard: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<DashboardCategory>(() => {
    return (localStorage.getItem("lastVisitedDashboard") as DashboardCategory) || "ALC";
  });

  const [allToursRaw, setAllToursRaw] = useState<any[]>([]);
  const [ongoingTours, setOngoingTours] = useState<TourHistory[]>([]);
  const [closedTours, setClosedTours] = useState<TourHistory[]>([]);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalTours: 0,
    metric2Label: "Lines On Hold",
    metric2Value: 0,
    metric2Color: "#dc2626",
    metric3Label: "Open Observations",
    metric3Value: 0,
    metric3Color: "#d97706",
    metric4Label: "Open Re-verifications",
    metric4Value: 0,
    metric4Color: "#2563eb"
  });

  const [qaConfigList, setQaConfigList] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alcPendingSync = useSelector((state: any) => state.ALC?.pendingSync || []);

  const currentUserEmail = accounts && accounts[0]?.username ? accounts[0].username.toLowerCase().trim() : "";
  const currentUserName = accounts && accounts[0]?.name ? accounts[0].name.toLowerCase().trim() : "";

  useEffect(() => {
    loadDashboardData();
  }, [instance, accounts]);

  useEffect(() => {
    localStorage.setItem("lastVisitedDashboard", selectedCategory);
    if (allToursRaw.length > 0) {
      processCategoryTours(allToursRaw, qaConfigList, selectedCategory);
    }
  }, [selectedCategory]);

  const resolveQaNameFromEmail = (email: string, configs: any[]) => {
    if (!email) return "N/A";
    if (configs && configs.length > 0) {
      for (const item of configs) {
        if (item.AssignedUser && item.AssignedUser.results) {
          const match = item.AssignedUser.results.find((u: any) => u.EMail && u.EMail.toLowerCase() === email.toLowerCase());
          if (match) return match.Title;
        }
      }
    }
    if (email.includes("@")) {
      const clean = email.split("@")[0].trim();
      const parts = clean.split(".");
      return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
    }
    return email;
  };

  const parseDateMoment = (dateStr: string) => {
    if (!dateStr) return moment.invalid();
    const formats = [
      "YYYY-MM-DDTHH:mm:ssZ",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
      "MM-DD-YYYY HH:mm:ss",
      "MM-DD-YYYY hh:mm A",
      "MM-DD-YYYY",
      "M/D/YYYY h:mm A",
      "M/D/YYYY hh:mm A",
      "MM/DD/YYYY hh:mm A",
      "MM/DD/YYYY HH:mm:ss",
      "MM/DD/YYYY",
      "M/D/YYYY",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY hh:mm A",
      "DD-MM-YYYY",
      "D/M/YYYY h:mm A",
      "D/M/YYYY hh:mm A",
      "DD/MM/YYYY hh:mm A",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY",
      "D/M/YYYY",
      "DD-MMM-YYYY hh:mm A",
      "DD-MMM-YYYY",
      "MM/DD/YYYY hh:mm:ss A",
      "DD/MM/YYYY hh:mm:ss A"
    ];
    const m = moment(dateStr, formats, true);
    if (m.isValid()) return m;
    
    // Fallback loose parsing
    const looseM = moment(dateStr);
    return looseM.isValid() ? looseM : moment.invalid();
  };

  const getAreaAssigneesForFailedCheckpoints = (t: any, configs: any[]) => {
    if (!t.checkpoints || t.checkpoints.length === 0 || !configs || configs.length === 0) {
      return [];
    }
    const pendingFailedCheckpoints = t.checkpoints.filter((cp: any) => {
      const isFailed = cp.cr3ea_status === "Not Okay" ||
        (cp.cr3ea_defectcategory && (
          cp.cr3ea_defectcategory.includes("00") ||
          cp.cr3ea_defectcategory.includes("01") ||
          cp.cr3ea_defectcategory.includes("Non-Compliant") ||
          cp.cr3ea_defectcategory.includes("Partial")
        ));
      const hasRemarks = cp.cr3ea_productionremarks || (cp.cr3ea_defectremarks && cp.cr3ea_defectremarks.trim().startsWith("Action:"));
      return isFailed && !hasRemarks;
    });

    if (pendingFailedCheckpoints.length === 0) return [];

    const assigneeNames = new Set<string>();
    pendingFailedCheckpoints.forEach((cp: any) => {
      const areaName = cp.cr3ea_area;
      if (!areaName) return;

      const configRow = configs.find(c =>
        c.ConfigType === "Product User" &&
        c.Area &&
        (c.Area.toLowerCase().includes(areaName.toLowerCase().trim()) ||
          areaName.toLowerCase().trim().includes(c.Area.toLowerCase()))
      );

      if (configRow && configRow.AssignedUser && configRow.AssignedUser.results) {
        configRow.AssignedUser.results.forEach((u: any) => {
          if (u.Title) assigneeNames.add(u.Title);
        });
      }
    });
    return Array.from(assigneeNames);
  };

  const calculateScoreDynamically = (t: any) => {
    if (!t.checkpoints || t.checkpoints.length === 0) return null;
    let totalMaxPoints = 0;
    let totalObtainedPoints = 0;
    t.checkpoints.forEach((cp: any) => {
      totalMaxPoints += 2;
      let numericScore = 2;
      const scoreText = cp.cr3ea_defectcategory || "";
      if (scoreText.includes("(0)") || scoreText === "00" || scoreText.includes("Non-Compliant")) {
        numericScore = 0;
      } else if (scoreText.includes("(1)") || scoreText === "01" || scoreText.includes("Partial")) {
        numericScore = 1;
      }
      totalObtainedPoints += numericScore;
    });
    if (totalMaxPoints === 0) return null;
    const percentRaw = (totalObtainedPoints / totalMaxPoints) * 100;
    return percentRaw.toFixed(2);
  };

  const resolveShiftExecNameFromEmail = (email: string, configs: any[], defaultName: string) => {
    if (!email || !configs) return defaultName;
    const shiftExecEmail = email.toLowerCase().trim();
    const relevantConfigs = configs.filter(c => 
        (c.ConfigType === "Line Master" || c.Config_x0020_Type === "Line Master") || 
        (c.ConfigType === "Product Master" || c.Config_x0020_Type === "Product Master")
    );
    for (const c of relevantConfigs) {
        if (c.ShiftExecutive && c.ShiftExecutive.results) {
            const match = c.ShiftExecutive.results.find((u: any) => u.EMail && u.EMail.toLowerCase().trim() === shiftExecEmail);
            if (match) return match.Title || match.name || defaultName;
        }
    }
    return defaultName;
  };

  const getPendingWith = (t: any, configs: any[]) => {
    let status = t.cr3ea_processstatus || t.cr3ea_status || "Pending QA";

    const qaNameRaw = t.cr3ea_tourby || t.cr3ea_assigned_qa || "QA Team";
    const qaName = qaNameRaw.includes("@") ? resolveQaNameFromEmail(qaNameRaw, configs) : qaNameRaw;

    const requestTimeString = t.cr3ea_request_time || t.cr3ea_tourstartdate || t.createdon;
    if (status === "Pending QA" && requestTimeString) {
      const reqTime = new Date(requestTimeString).getTime();
      const now = new Date().getTime();
      if (!isNaN(reqTime) && (now - reqTime > 5 * 60 * 1000)) {
        const tourDateLocal = moment(requestTimeString).local().format("YYYY-MM-DD");
        const todayLocal = moment().format("YYYY-MM-DD");
        if (tourDateLocal === todayLocal) {
          status = "Escalated";
        }
      }
    }

    const shiftExecEmail = t.cr3ea_shiftexecutiveproduction ? String(t.cr3ea_shiftexecutiveproduction).toLowerCase().trim() : "";
    const shiftExecFallback = t.cr3ea_shiftexecutiveproduction ? (t.cr3ea_shiftexecutiveproduction.split("@")[0].split(".")[0] || "") : "";
    const shiftExecName = resolveShiftExecNameFromEmail(shiftExecEmail, configs, shiftExecFallback);

    switch (status) {
      case "Escalated":
        return `Escalation Manager`;
      case "Pending QA":
        return `QA Incharge (${qaName})`;
      case "QA In Progress":
      case "In Progress":
      case "InProgress-paused":
        return `QA Executive (${qaName})`;
      case "Pending Production":
      case "Failed - Pending Production":
      case "Success - Pending Production":
        const assignees = getAreaAssigneesForFailedCheckpoints(t, configs);
        if (assignees && assignees.length > 0) {
          return `Production Exec (${assignees.join(", ")})`;
        }
        return `Production Exec (${shiftExecName || "N/A"})`;
      case "Pending Re-Verification":
      case "Success - Pending Re-Verification":
      case "Failed - Pending Re-Verification":
        const pendingProds = getAreaAssigneesForFailedCheckpoints(t, configs);
        if (pendingProds && pendingProds.length > 0) {
          return `QA Executive (${qaName}) & Production Exec (${pendingProds.join(", ")})`;
        }
        return `Production Exec (${shiftExecName || "N/A"})`;
      default:
        return "Production Team";
    }
  };

  const processCategoryTours = (
    allList: any[],
    configs: any[],
    category: DashboardCategory
  ) => {
    const isFS = category === "FoodSafety";
    const isCCP = category === "CCP_OPRP_Sieves";
    const isMB = category === "MixingAndBaking";

    const filteredList = allList.filter(t => {
      const titleVal = String(t.cr3ea_title || "");
      let cleanTitle = titleVal.split("||")[0].trim();
      const isFSPrefix = cleanTitle.startsWith("FoodSafety_") || cleanTitle.startsWith("Food_Safety_");
      if (isFSPrefix) {
        cleanTitle = cleanTitle.replace("FoodSafety_", "").replace("Food_Safety_", "");
      }
      const isFSTitle = isFSPrefix || cleanTitle.startsWith("PPE_") || cleanTitle.startsWith("GMP_") || cleanTitle.startsWith("PCI_") || cleanTitle.toLowerCase().includes("checklist");
      const hasFSField = t.cr3ea_food_safety_checklisttype;
      const isFSItem = !!hasFSField || isFSTitle;

      const hasCCPField = t.cr3ea_ccp_oprp_sieves_parametertype;
      const isCCPTitle = cleanTitle.startsWith("CCP_") || cleanTitle.startsWith("Sieves_") || cleanTitle.toLowerCase().includes("ccp") || cleanTitle.toLowerCase().includes("sieves");
      const isCCPItem = !!hasCCPField || isCCPTitle;

      const isMBTitle = cleanTitle.startsWith("MixingBaking_") || cleanTitle.startsWith("Mixing_") || cleanTitle.toLowerCase().includes("mixing") || cleanTitle.toLowerCase().includes("baking");
      const hasMBField = t.cr3ea_mixing_baking_productname;
      const isMBItem = !!hasMBField || isMBTitle;

      const isPkgopsTitle = ["Temperatures & Humidity", "Code Verification", "PAPA", "PQI", "Seal Integrity", "Cream Percentage", "Quality Wall Records"].some(p => cleanTitle.includes(p)) || cleanTitle.toLowerCase().includes("pkgops") || cleanTitle.toLowerCase().includes("packaging");
      const hasPkgOpsField = t.cr3ea_pkgops_type;
      const isPKGOPSItem = !!hasPkgOpsField || isPkgopsTitle;

      const isAlcTitle = cleanTitle.startsWith("ALC_") || cleanTitle.startsWith("ALC") || cleanTitle.toLowerCase().includes("line clearance") || cleanTitle.toLowerCase().includes("changeover") || cleanTitle.toLowerCase().includes("area line clearance");
      const hasAlcField = t.cr3ea_previousrunningvariety !== undefined || t.cr3ea_runningvariety !== undefined || t.cr3ea_islineclear !== undefined;
      const isALCItem = (isAlcTitle || hasAlcField) && !isFSItem && !isCCPItem && !isMBItem && !isPKGOPSItem;

      if (isFS) return isFSItem;
      if (isCCP) return isCCPItem;
      if (isMB) return isMBItem;
      if (category === "PackagingOperations") return isPKGOPSItem;
      if (category === "ALC") return isALCItem;
      return false;
    });

    const ongoingRaw: any[] = [];
    const closedRaw: any[] = [];

    let metric2 = 0;
    let metric3 = 0;
    let metric4 = 0;

    for (const t of filteredList) {
      let status = t.cr3ea_processstatus || t.cr3ea_status || "In Progress";
      const isTerminal = status === "Completed" || status === "Closed" || status === "Closed - Expired" || status === "Success" || status === "Success - Expired" || status === "Failed - Expired" || status === "Failed" || status === "Submitted";

      if (isTerminal) {
        closedRaw.push(t);
        if (isFS) {
          if (t.cr3ea_checklist_result === "Pass") metric4++;
          else if (t.cr3ea_checklist_result === "Fail") metric2++;
        } else if (isCCP || isMB) {
          metric4++;
        }
      } else {
        ongoingRaw.push(t);
        if (isFS || isMB) {
          metric3++;
        } else if (isCCP) {
          metric3++;
          if (t.cr3ea_escalated === "Yes" || t.cr3ea_status === "Escalated") {
            metric2++;
          }
        }
      }
    }

    if (!isFS && !isCCP && !isMB) {
      metric2 = filteredList.filter(t => {
        const titleVal = t.cr3ea_title || "";
        const cleanTitle = titleVal.split("||")[0].trim();
        const form = cleanTitle.split('_')[0] || "Area Line Clearance";
        const isAlcOrPkg = form.toLowerCase().includes("line") || form.toLowerCase().includes("alc") || form.toLowerCase().includes("clearance") || form.toLowerCase().includes("pkg") || form.toLowerCase().includes("packaging") || t.cr3ea_pkgops_type;
        if (!isAlcOrPkg) return false;

        const status = t.cr3ea_processstatus || t.cr3ea_status || "In Progress";
        const isTerminal = status === "Completed" || status === "Closed" || status === "Closed - Expired" || status === "Success" || status === "Success - Expired";
        if (isTerminal) return false;

        const isClearedVal = t.cr3ea_islineclear;
        return (isClearedVal === false || isClearedVal === "false" || isClearedVal === 0 || isClearedVal === "0" || isClearedVal === "No");
      }).length;

      metric3 = filteredList.filter(t => {
          const s = t.cr3ea_status || t.cr3ea_processstatus || "";
          return s.includes("Pending Production") || s === "Failed - Pending Production" || s === "Success - Pending Production" || s.includes("Pending Observation");
      }).length;

      metric4 = filteredList.filter(t => {
          const s = t.cr3ea_status || t.cr3ea_processstatus || "";
          return s.includes("Pending Re-Verification") || s === "Pending Re-Verification" || s === "Success - Pending Re-Verification" || s === "Failed - Pending Re-Verification";
      }).length;
    }

    if (isFS) {
      setMetrics({
        totalTours: filteredList.length,
        metric2Label: "Failed Audits",
        metric2Value: metric2,
        metric2Color: "#dc2626",
        metric3Label: "In Progress",
        metric3Value: metric3,
        metric3Color: "#d97706",
        metric4Label: "Passed Audits",
        metric4Value: metric4,
        metric4Color: "#16a34a"
      });
    } else if (isCCP) {
      setMetrics({
        totalTours: filteredList.length,
        metric2Label: "Overdue Tours",
        metric2Value: metric2,
        metric2Color: "#dc2626",
        metric3Label: "In Progress",
        metric3Value: metric3,
        metric3Color: "#d97706",
        metric4Label: "Completed Tours",
        metric4Value: metric4,
        metric4Color: "#16a34a"
      });
    } else if (isMB) {
      setMetrics({
        totalTours: filteredList.length,
        metric2Label: "Deviated Batches",
        metric2Value: metric2,
        metric2Color: "#dc2626",
        metric3Label: "In Progress",
        metric3Value: metric3,
        metric3Color: "#d97706",
        metric4Label: "Completed Tours",
        metric4Value: metric4,
        metric4Color: "#16a34a"
      });
    } else {
      setMetrics({
        totalTours: filteredList.length,
        metric2Label: "Lines On Hold",
        metric2Value: metric2,
        metric2Color: "#dc2626",
        metric3Label: "Open Observations",
        metric3Value: metric3,
        metric3Color: "#d97706",
        metric4Label: "Open Re-verifications",
        metric4Value: metric4,
        metric4Color: "#2563eb"
      });
    }

    const processTourRow = (t: any): TourHistory => {
      const parsedM = parseDateMoment(t.cr3ea_tourstartdate || t.createdon);
      const date = parsedM.isValid() ? parsedM.format("DD-MM-YYYY hh:mm A") : "N/A";
      const line = t.cr3ea_lineno || "N/A";
      const shift = t.cr3ea_shift || "N/A";
      let status = t.cr3ea_processstatus || t.cr3ea_status || "In Progress";

      let form = "ALC";
      let execs = "";
      let scoreDisplay: any = "-";
      let isLineClearBadge: React.ReactNode = undefined;
      let cycleBadge: React.ReactNode = undefined;
      let freqBadge: React.ReactNode = undefined;
      let pendingWith = "N/A";
      let result = t.cr3ea_checklist_result || "N/A";
      let isMyTask = false;
      let isEscalatedForMe = false;
      let isClickable = true;

      const tourId = getRajpuraTourId(t);

      if (isFS) {
        let rawForm = t.cr3ea_food_safety_checklisttype;
        if (!rawForm && t.cr3ea_title) {
          let cleanTitle = t.cr3ea_title.split("||")[0].trim();
          if (cleanTitle.startsWith("FoodSafety_")) cleanTitle = cleanTitle.replace("FoodSafety_", "");
          if (cleanTitle.startsWith("Food_Safety_")) cleanTitle = cleanTitle.replace("Food_Safety_", "");
          if (cleanTitle.startsWith("PPE_")) rawForm = "PPE Checklist";
          else if (cleanTitle.startsWith("GMP_")) rawForm = "GMP Checklist";
          else if (cleanTitle.startsWith("PCI_")) rawForm = "PCI Checklist";
        }
        form = rawForm || "Food Safety Checklist";

        const qaName = t.cr3ea_assigned_qa || t.cr3ea_tourby || "N/A";
        const qaExec = qaName.includes("@") ? resolveQaNameFromEmail(qaName, configs) : qaName;
        const prodInchargeRaw = t.cr3ea_shiftexecutiveproduction || "N/A";
        const prodIncharge = prodInchargeRaw.includes("@") ? resolveShiftExecNameFromEmail(prodInchargeRaw, configs, prodInchargeRaw.split("@")[0].split(".")[0]) : prodInchargeRaw;
        execs = `QA: ${qaExec} | Prod: ${prodIncharge}`;

        if (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null) {
          scoreDisplay = <strong style={{ color: '#0f172a', fontSize: '14px' }}>{t.cr3ea_overall_score}</strong>;
        }

        cycleBadge = (
          <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
            {t.cr3ea_food_safety_cycle || "Cycle-1"}
          </span>
        );

        const isProgress = (status.toLowerCase() === "in-progress" || status.toLowerCase() === "in progress" || status.toLowerCase() === "inprogress-paused");
        pendingWith = isProgress ? `QA Executive (${qaExec})` : "Completed";

        const isQaStatus = (status === "Pending QA" || status === "QA In Progress" || status === "In Progress" || status === "InProgress-paused" || status === "Pending Re-Verification");
        if (isQaStatus) {
          const qaEmail = (t.cr3ea_assigned_qa || t.cr3ea_tourby || "").toLowerCase().trim();
          const qaResolvedName = qaEmail.includes("@") ? resolveQaNameFromEmail(qaEmail, configs).toLowerCase().trim() : qaEmail;
          if ((currentUserEmail && qaEmail && (currentUserEmail === qaEmail || currentUserEmail.includes(qaEmail))) ||
            (currentUserName && qaEmail && (currentUserName === qaEmail || currentUserName.includes(qaEmail) || qaEmail.includes(currentUserName))) ||
            (currentUserName && qaResolvedName && (currentUserName === qaResolvedName || currentUserName.includes(qaResolvedName) || qaResolvedName.includes(currentUserName)))) {
            isMyTask = true;
          }
        }

        isClickable = true;

      } else if (isCCP) {
        let rawForm = t.cr3ea_ccp_oprp_sieves_parametertype;
        if (!rawForm && t.cr3ea_title) {
          const cleanTitle = String(t.cr3ea_title).split("||")[0].trim();
          if (cleanTitle.includes("Sieves") || cleanTitle.includes("sieves")) rawForm = "Sieves & Magnets";
          else rawForm = "CCP & OPRP";
        }
        form = rawForm || "CCP, OPRP & Sieves";

        const qaName = t.cr3ea_assigned_qa || t.cr3ea_tourby || "N/A";
        const qaExec = qaName.includes("@") ? resolveQaNameFromEmail(qaName, configs) : qaName;
        const prodInchargeRaw = t.cr3ea_shiftexecutiveproduction || "N/A";
        const prodIncharge = prodInchargeRaw.includes("@") ? resolveShiftExecNameFromEmail(prodInchargeRaw, configs, prodInchargeRaw.split("@")[0].split(".")[0]) : prodInchargeRaw;
        execs = `QA: ${qaExec} | Prod: ${prodIncharge}`;

        if (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null) {
          scoreDisplay = <strong style={{ color: '#0f172a', fontSize: '14px' }}>{t.cr3ea_overall_score}</strong>;
        }

        const freqText = t.cr3ea_ccp_oprp_sieves_frequency || t.cr3ea_ccp_oprp_sieves_productvariety || "2-Hour Check";
        freqBadge = (
          <span className="badge" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
            {freqText}
          </span>
        );

        const isProgress = (String(status).toLowerCase() === "in-progress" || String(status).toLowerCase() === "in progress" || String(status).toLowerCase() === "inprogress-paused");
        pendingWith = isProgress ? `QA Executive (${qaExec})` : status;

        const isQaStatus = (status === "In Progress" || status === "InProgress-paused" || status === "Escalated");
        if (isQaStatus) {
          const qaEmail = String(t.cr3ea_assigned_qa || t.cr3ea_tourby || "").toLowerCase().trim();
          const qaResolvedName = qaEmail.includes("@") ? resolveQaNameFromEmail(qaEmail, configs).toLowerCase().trim() : qaEmail;
          if ((currentUserEmail && qaEmail && (currentUserEmail === qaEmail || currentUserEmail.includes(qaEmail))) ||
            (currentUserName && qaEmail && (currentUserName === qaEmail || currentUserName.includes(qaEmail) || qaEmail.includes(currentUserName))) ||
            (currentUserName && qaResolvedName && (currentUserName === qaResolvedName || currentUserName.includes(qaResolvedName) || qaResolvedName.includes(currentUserName)))) {
            isMyTask = true;
          }
        }

        if ((status === "In Progress" || status === "InProgress-paused") && !isMyTask) {
          isClickable = false;
        }

      } else if (isMB) {
        let rawForm = "Mixing & Baking";
        if (t.cr3ea_mixing_baking_productname) {
          rawForm = `Mixing & Baking (${t.cr3ea_mixing_baking_productname})`;
        }
        form = rawForm;

        const qaName = t.cr3ea_assigned_qa || t.cr3ea_tourby || "N/A";
        const qaExec = qaName.includes("@") ? resolveQaNameFromEmail(qaName, configs) : qaName;
        const prodInchargeRaw = t.cr3ea_shiftexecutiveproduction || "N/A";
        const prodIncharge = prodInchargeRaw.includes("@") ? resolveShiftExecNameFromEmail(prodInchargeRaw, configs, prodInchargeRaw.split("@")[0].split(".")[0]) : prodInchargeRaw;
        execs = `QA: ${qaExec} | Prod: ${prodIncharge}`;

        scoreDisplay = <strong style={{ color: '#0284c7', fontSize: '13px' }}>{t.cr3ea_mixing_baking_batchno ? `Batch: ${t.cr3ea_mixing_baking_batchno}` : "Active"}</strong>;

        cycleBadge = (
          <span className="badge" style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
            {t.cr3ea_cycle || "Cycle-1"}
          </span>
        );

        const isProgress = (String(status).toLowerCase() === "in-progress" || String(status).toLowerCase() === "in progress" || String(status).toLowerCase() === "inprogress-paused");
        pendingWith = isProgress ? `QA Executive (${qaExec})` : "Completed";

        const isQaStatus = (status === "In Progress" || status === "InProgress-paused" || status === "Pending QA");
        if (isQaStatus) {
          const qaEmail = String(t.cr3ea_assigned_qa || t.cr3ea_tourby || "").toLowerCase().trim();
          const qaResolvedName = qaEmail.includes("@") ? resolveQaNameFromEmail(qaEmail, configs).toLowerCase().trim() : qaEmail;
          if ((currentUserEmail && qaEmail && (currentUserEmail === qaEmail || currentUserEmail.includes(qaEmail))) ||
            (currentUserName && qaEmail && (currentUserName === qaEmail || currentUserName.includes(qaEmail) || qaEmail.includes(currentUserName))) ||
            (currentUserName && qaResolvedName && (currentUserName === qaResolvedName || currentUserName.includes(qaResolvedName) || qaResolvedName.includes(currentUserName)))) {
            isMyTask = true;
          }
        }

        isClickable = true;

      } else {
        // ALC & Packaging Operations
        const titleVal = t.cr3ea_title || "";
        const cleanTitle = titleVal.split("||")[0].trim();
        const rawForm = cleanTitle.split('_')[0] || "Area Line Clearance";
        const isPkgopsTitle = ["Temperatures & Humidity", "Code Verification", "PAPA", "PQI", "Seal Integrity", "Cream Percentage", "Quality Wall Records"].some(p => cleanTitle.includes(p)) || cleanTitle.toLowerCase().includes("pkgops") || cleanTitle.toLowerCase().includes("packaging");
        const hasPkgOpsField = t.cr3ea_pkgops_type;
        const isPkgOps = !!hasPkgOpsField || isPkgopsTitle;

        if (isPkgOps) {
            const subType = t.cr3ea_pkgops_type || "Packaging Operations";
            form = `Packaging - ${subType}`;
        } else {
            form = rawForm.includes("Area") || rawForm.includes("Line") || rawForm.includes("ALC") ? "ALC" : rawForm;
        }
        
        let prodExec = t.cr3ea_shiftexecutiveproduction || "N/A";
        let qaExecRaw = t.cr3ea_tourby || "N/A";
        let qaExec = qaExecRaw.includes("@") ? resolveQaNameFromEmail(qaExecRaw, configs) : qaExecRaw;

        if (!isPkgOps && t.cr3ea_observedby && t.cr3ea_observedby.includes("||")) {
          prodExec = t.cr3ea_observedby.split("||")[0]?.trim() || prodExec;
        }
        execs = `Shift: ${prodExec} | QA: ${qaExec}`;

        pendingWith = getPendingWith(t, configs);

        let scoreNum: number | null = null;
        const computedScore = calculateScoreDynamically(t);
        
        let titleScoreStr: string | null = null;
        if (titleVal && titleVal.includes("||")) {
          const titleParts = titleVal.split("||");
          const scorePart = titleParts[1] ? titleParts[1].trim() : "";
          if (scorePart.startsWith("Score:")) {
            titleScoreStr = scorePart.replace("Score:", "").trim();
          } else if (scorePart) {
            titleScoreStr = scorePart;
          }
        }

        if (computedScore !== null) {
          scoreNum = parseFloat(computedScore);
          scoreDisplay = <strong style={{ color: '#0f172a', fontSize: '14px' }}>{computedScore}%</strong>;
        } else if (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null && String(t.cr3ea_overall_score).trim() !== "") {
          scoreNum = parseFloat(t.cr3ea_overall_score);
          scoreDisplay = <strong style={{ color: '#0f172a', fontSize: '14px' }}>{scoreNum.toFixed(2)}%</strong>;
        } else if (titleScoreStr && titleScoreStr !== "N/A") {
          scoreNum = parseFloat(titleScoreStr.replace("%", ""));
          if (!isNaN(scoreNum)) {
             scoreDisplay = <strong style={{ color: '#0f172a', fontSize: '14px' }}>{scoreNum.toFixed(2)}%</strong>;
          } else {
             scoreNum = null;
          }
        }

        let isEscalated = false;
        const requestTimeString = t.cr3ea_request_time || t.cr3ea_tourstartdate || t.createdon;
        if (status === "Pending QA" && requestTimeString) {
          const reqTime = new Date(requestTimeString).getTime();
          const now = new Date().getTime();
          if (!isNaN(reqTime) && (now - reqTime > 5 * 60 * 1000)) {
            const tourDateLocal = moment(requestTimeString).local().format("YYYY-MM-DD");
            const todayLocal = moment().format("YYYY-MM-DD");
            if (tourDateLocal === todayLocal) {
              status = "Escalated";
              isEscalated = true;
            }
          }
        }

        let hasCriticalFailure = false;
        if (t.checkpoints && Array.isArray(t.checkpoints) && t.checkpoints.length > 0) {
          const questionConfigs = (configs || []).filter(c => c.ConfigType === "Checklist Question");
          t.checkpoints.forEach((cp: any) => {
            const scoreText = cp.cr3ea_defectcategory || "";
            const isFailedScore = (scoreText.includes("(0)") || scoreText === "00" || scoreText.includes("Non-Compliant") || scoreText.includes("(1)") || scoreText === "01" || scoreText.includes("Partial"));
            if (isFailedScore) {
              let isCritical = false;
              const criteriaText = (cp.cr3ea_criteria || "").toLowerCase().trim();
              const matchedCfg = questionConfigs.find(q => {
                const qTitle = (q.Title || "").toLowerCase().trim();
                const qRemarks = (q.Remarks || "").toLowerCase().trim();
                return (qTitle && (criteriaText.includes(qTitle) || qTitle.includes(criteriaText))) ||
                       (qRemarks && (criteriaText.includes(qRemarks) || qRemarks.includes(criteriaText)));
              });
              if (matchedCfg) {
                isCritical = !!(matchedCfg.isCritical || matchedCfg.IsCritical === true || matchedCfg.ProductCategory === "Critical" || matchedCfg.Remarks === "Critical");
              }
              if (isCritical) {
                hasCriticalFailure = true;
              }
            }
          });
        }

        const isLineClearVal = t.cr3ea_islineclear;
        const isLineClearExplicitNo = (isLineClearVal === false || isLineClearVal === "false" || isLineClearVal === 0 || isLineClearVal === "0" || isLineClearVal === "No");
        const isChecklistResultFail = (t.cr3ea_checklist_result === "Fail" || t.cr3ea_checklist_result === "Expired");
        const isExplicitlyFailedStatus = (String(t.cr3ea_processstatus || "").startsWith("Failed") || String(t.cr3ea_status || "").startsWith("Failed"));
        const isEvaluationDone = (scoreNum !== null || isChecklistResultFail || isExplicitlyFailedStatus || status.includes("Pending Production") || status.includes("Pending Re-Verification"));
        const isTourFailed = hasCriticalFailure || (isEvaluationDone && (isLineClearExplicitNo || isChecklistResultFail || isExplicitlyFailedStatus || (scoreNum !== null && scoreNum < 80)));

        if (status.includes("Pending Production") || status === "Pending Production") {
          const isSuccess = (scoreNum !== null && scoreNum >= 80 && !isTourFailed);
          status = isSuccess ? "Success - Pending Production" : "Failed - Pending Production";
        } else if (status.includes("Pending Re-Verification") || status === "Pending Re-Verification") {
          const isSuccess = (scoreNum !== null && scoreNum >= 80 && !isTourFailed);
          status = isSuccess ? "Success - Pending Re-Verification" : "Failed - Pending Re-Verification";
        } else if (status === "Closed - Expired") {
          const isSuccess = (scoreNum !== null && scoreNum >= 80 && !isTourFailed);
          status = isSuccess ? "Success - Expired" : "Failed - Expired";
        }

        if (scoreNum === null) {
          if (status === "In Progress") scoreDisplay = <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>Request Pending</span>;
          else if (status === "Pending QA") scoreDisplay = <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>Awaiting QA Accept</span>;
          else if (status === "Escalated") scoreDisplay = <span className="text-danger fw-bold" style={{ fontSize: '12px' }}>DELAYED</span>;
          else if (status === "QA In Progress" || status.startsWith("QA In Progress")) scoreDisplay = <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>Evaluation Pending</span>;
          else scoreDisplay = <span className="text-muted" style={{ fontSize: '12px', fontStyle: 'italic' }}>N/A</span>;
        }

        isLineClearBadge = "-";
        const isAlcTour = (form.toLowerCase().includes("line") || form.toLowerCase().includes("alc") || form.toLowerCase().includes("clearance"));
        if (isAlcTour) {
            const isCleared = !isTourFailed && (
                isLineClearVal === true ||
                isLineClearVal === "true" ||
                isLineClearVal === 1 ||
                isLineClearVal === "1" ||
                isLineClearVal === "Yes" ||
                status === "Completed" ||
                status === "Closed" ||
                status === "Closed - Expired" ||
                status === "Success" ||
                status === "Success - Expired"
            );

            isLineClearBadge = isCleared ? (
                <span style={{ backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
                    Yes
                </span>
            ) : (
                <span style={{ backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
                    No
                </span>
            );
        }

        const isQaStatus = (status === "Pending QA" || status === "QA In Progress" || status.startsWith("QA In Progress") || status === "Pending Re-Verification" || status === "Success - Pending Re-Verification" || status === "Failed - Pending Re-Verification");
        if (isQaStatus) {
          const qaEmail = (t.cr3ea_assigned_qa || t.cr3ea_tourby || "").toLowerCase().trim();
          const qaResolvedName = qaEmail.includes("@") ? resolveQaNameFromEmail(qaEmail, configs).toLowerCase().trim() : qaEmail;
          if ((currentUserEmail && qaEmail && (currentUserEmail === qaEmail || currentUserEmail.includes(qaEmail))) ||
            (currentUserName && qaEmail && (currentUserName === qaEmail || currentUserName.includes(qaEmail) || qaEmail.includes(currentUserName))) ||
            (currentUserName && qaResolvedName && (currentUserName === qaResolvedName || currentUserName.includes(qaResolvedName) || qaResolvedName.includes(currentUserName)))) {
            isMyTask = true;
          }
        }
        
        const escalationContactsStr = t.cr3ea_escalation_contacts || "";
        const escalationEmails = escalationContactsStr.toLowerCase().split(",").map((e: string) => e.trim());

        const qaEmailVal = (t.cr3ea_assigned_qa || t.cr3ea_tourby || "").toLowerCase().trim();
        if (qaEmailVal && configs) {
            const matchConfig = configs.find(c =>
                c.AssignedUser && c.AssignedUser.results &&
                c.AssignedUser.results.some((u: any) => u.EMail && u.EMail.toLowerCase().trim() === qaEmailVal)
            );
            if (matchConfig && matchConfig.EscalationManager && matchConfig.EscalationManager.results) {
                matchConfig.EscalationManager.results.forEach((m: any) => {
                    if (m.EMail) {
                        const emailLower = m.EMail.toLowerCase().trim();
                        if (!escalationEmails.includes(emailLower)) {
                            escalationEmails.push(emailLower);
                        }
                    }
                });
            }
        }

        const isUserEscalationManager = currentUserEmail && escalationEmails.includes(currentUserEmail);
        const isUserEscalationManagerByName = currentUserName && escalationEmails.some((email: string) => email.includes(currentUserName));

        isEscalatedForMe = isEscalated && (isUserEscalationManager || isUserEscalationManagerByName);

        const isProdStatus = (status === "Failed - Pending Production" || status === "Success - Pending Production");
        const isReverifyStatus = (status === "Pending Re-Verification" || status === "Success - Pending Re-Verification" || status === "Failed - Pending Re-Verification");
        let isAlcAssigneeMatch = false;

        if (isProdStatus || isReverifyStatus) {
          const prodExecName = (t.cr3ea_shiftexecutiveproduction || "").toLowerCase().trim();
          const assignees = getAreaAssigneesForFailedCheckpoints(t, configs);

          const isUserProdExec = (currentUserName && prodExecName && (prodExecName === currentUserName || prodExecName.includes(currentUserName) || currentUserName.includes(prodExecName))) ||
            (currentUserEmail && prodExecName && currentUserEmail.includes(prodExecName.replace(/\s+/g, ".")));

          isAlcAssigneeMatch = assignees && assignees.some(name => {
            const cleanName = name.toLowerCase().trim();
            return cleanName === currentUserName ||
              (currentUserName && (cleanName.includes(currentUserName) || currentUserName.includes(cleanName))) ||
              (currentUserEmail && currentUserEmail.includes(cleanName.replace(/\s+/g, ".")));
          }) || false;

          if (isProdStatus) {
            const hasNoPendingForThisAreaOwner = assignees && assignees.length > 0 && !isAlcAssigneeMatch;
            if (isUserProdExec || !hasNoPendingForThisAreaOwner) {
              isMyTask = true;
            }
          }
        }

        const isAlcCancelled = String(status).toLowerCase().includes("cancel") || String(t.cr3ea_processstatus || "").toLowerCase().includes("cancel");
        const isPkgInProgress = (status === "QA In Progress" || status.startsWith("QA In Progress") || status === "In Progress" || status === "InProgress-paused" || status === "Pending QA");
        
        isClickable = true;
        if (isAlcCancelled) {
            isClickable = false;
        } else if (isPkgOps) {
            // Packaging ops specific
            const qaValues = [t.cr3ea_assigned_qa, t.cr3ea_tourby, t.cr3ea_qaexecutive].filter(Boolean);
            const isAssignedQA = qaValues.some((val: string) => {
                if (!val) return false;
                const raw = String(val).toLowerCase().trim();
                const resolved = raw.includes("@") ? resolveQaNameFromEmail(raw, configs).toLowerCase().trim() : raw;
                if (currentUserEmail && (raw === currentUserEmail || currentUserEmail.includes(raw) || raw.includes(currentUserEmail))) return true;
                if (currentUserName && (raw === currentUserName || currentUserName.includes(raw) || raw.includes(currentUserName))) return true;
                if (currentUserName && (resolved === currentUserName || currentUserName.includes(resolved) || resolved.includes(currentUserName))) return true;
                return false;
            });
            const prodValues = [t.cr3ea_shiftexecutiveproduction, t.cr3ea_shiftexecutive, t.cr3ea_production_incharge, t.cr3ea_observedby].filter(Boolean);
            const isUserProdExec = prodValues.some((val: string) => {
                if (!val) return false;
                const raw = String(val).toLowerCase().trim();
                if (currentUserEmail && (raw === currentUserEmail || currentUserEmail.includes(raw) || raw.includes(currentUserEmail))) return true;
                if (currentUserName && (raw === currentUserName || currentUserName.includes(raw) || raw.includes(currentUserName))) return true;
                return false;
            });
            
            const isPkgReverify = (status.includes("Pending Re-Verification") || status === "Pending Re-Verification" || status === "Success - Pending Re-Verification" || status === "Failed - Pending Re-Verification");
            const isPkgProdPending = (status.includes("Pending Production") || status.includes("Pending Observation") || status === "Failed - Pending Production" || status === "Success - Pending Production" || status === "Production Action Needed");
            
            if (isPkgInProgress && !isAssignedQA) {
                isClickable = false;
            } else if (isPkgReverify && !isAssignedQA) {
                isClickable = false;
            } else if (isPkgProdPending && !isUserProdExec) {
                isClickable = false;
            }
        } else {
            // ALC specific
            if ((status === "QA In Progress" || status.startsWith("QA In Progress")) && !isMyTask) {
                isClickable = false;
            } else if (isReverifyStatus) {
                const prodExecName = (t.cr3ea_shiftexecutiveproduction || "").toLowerCase().trim();
                const isUserProdExec = (currentUserName && prodExecName && (prodExecName === currentUserName || prodExecName.includes(currentUserName) || currentUserName.includes(prodExecName))) ||
                  (currentUserEmail && prodExecName && currentUserEmail.includes(prodExecName.replace(/\s+/g, ".")));
                
                if ((isUserProdExec || isAlcAssigneeMatch) && !isMyTask) {
                    isClickable = false;
                }
            }
        }
      }

      const hasPendingSync = alcPendingSync.some((p: any) =>
        (p.type === 'SESSION_UPDATE' && p.payload.cr3ea_prod_qualitytourid === tourId) ||
        (p.type === 'SESSION_CREATE' && p.payload.cr3ea_prod_qualitytourid === tourId)
      );
      if (hasPendingSync) {
        status = "Pending Sync";
        pendingWith = "Local Device (Offline)";
      }

      if (status === "Completed") status = "Success";

      return {
        id: tourId,
        date,
        form,
        line,
        shift,
        execs,
        score: scoreDisplay,
        isLineClearBadge,
        cycleBadge,
        freqBadge,
        result,
        status,
        pendingWith,
        isMyTask,
        isEscalatedForMe,
        isClickable,
        rawTour: t
      };
    };

    const processClosedTourRow = (t: any): TourHistory => {
      const parsedM = parseDateMoment(t.cr3ea_tourstartdate || t.createdon);
      const date = parsedM.isValid() ? parsedM.format("DD-MM-YYYY hh:mm A") : "N/A";
      const titleVal = t.cr3ea_title || "";
      const cleanTitle = titleVal.split("||")[0].trim();
      let form = cleanTitle.split('_')[0] || "Area Line Clearance";
      const isAlcTour = (form.toLowerCase().includes("line") || form.toLowerCase().includes("alc") || form.toLowerCase().includes("clearance"));
      if (isAlcTour) form = "ALC";
      
      const line = t.cr3ea_lineno || "N/A";
      const shift = t.cr3ea_shift || "N/A";
      
      const isFS = t.cr3ea_food_safety_checklisttype || cleanTitle.includes("FoodSafety_");
      const isMB = cleanTitle.includes("MixingAndBaking_");
      const isCCP = cleanTitle.includes("CCP_");
      const isPkgOps = cleanTitle.includes("Packaging_");

      let qaName = t.cr3ea_assigned_qa || t.cr3ea_tourby || "N/A";
      let prodInchargeRaw = t.cr3ea_shiftexecutiveproduction || "N/A";

      const qaExec = qaName.includes("@") ? resolveQaNameFromEmail(qaName, configs) : qaName;
      const prodIncharge = prodInchargeRaw.includes("@") ? resolveShiftExecNameFromEmail(prodInchargeRaw, configs, prodInchargeRaw.split("@")[0].split(".")[0]) : prodInchargeRaw;
      const execs = `Shift: ${prodIncharge} | QA: ${qaExec}`;

      let status = t.cr3ea_processstatus || t.cr3ea_status || "Completed";
      const isCancelled = String(status).toLowerCase().includes("cancel");
      const tourId = getRajpuraTourId(t);

      let scoreDisplay: React.ReactNode = "N/A";
      let result = t.cr3ea_checklist_result || "N/A";
      let isLineClearBadge: React.ReactNode = "-";
      let cycleBadge: React.ReactNode = null;
      let freqBadge: React.ReactNode = null;

      if (isFS) {
          form = t.cr3ea_food_safety_checklisttype || "Food Safety Checklist";
          scoreDisplay = (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null) ? t.cr3ea_overall_score : "-";
          cycleBadge = <span style={{ backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>{t.cr3ea_food_safety_cycle || "Cycle-1"}</span>;
          if (isCancelled) status = "Cancelled";
          else if (status === "Fail") status = "Fail";
          else status = "Completed";
      } else if (isMB) {
          form = "Mixing & Baking";
          scoreDisplay = "-";
          isLineClearBadge = <span style={{ backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>Mixing & Baking</span>;
          if (isCancelled) status = "Cancelled";
      } else if (isCCP) {
          form = t.cr3ea_ccp_oprp_sieves_parametertype || "CCP, OPRP & Sieves";
          scoreDisplay = "-";
          const freqText = t.cr3ea_ccp_oprp_sieves_frequency || t.cr3ea_ccp_oprp_sieves_productvariety || "2-Hour Check";
          isLineClearBadge = <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>{freqText}</span>;
          if (isCancelled) status = "Cancelled";
      } else if (isPkgOps) {
          form = `Packaging - ${t.cr3ea_pkgops_type || "Packaging Operations"}`;
          if (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null && String(t.cr3ea_overall_score).trim() !== "") {
              scoreDisplay = `${parseFloat(t.cr3ea_overall_score).toFixed(2)}%`;
          } else {
              scoreDisplay = "-";
          }
          const isClearedVal = t.cr3ea_islineclear;
          const isCleared = (isClearedVal === true || isClearedVal === "true" || isClearedVal === 1 || isClearedVal === "1" || isClearedVal === "Yes");
          isLineClearBadge = isCleared ? (
            <span style={{ backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
              <span style={{marginRight: '4px'}}>○</span>Yes
            </span>
          ) : (
            <span style={{ backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>
              <span style={{marginRight: '4px'}}>○</span>No
            </span>
          );
          if (isCancelled) status = "Cancelled";
      } else {
          // ALC Closed Formatting
          let rawScore = "-";
          if (t.cr3ea_overall_score !== undefined && t.cr3ea_overall_score !== null) {
              rawScore = `${parseFloat(t.cr3ea_overall_score).toFixed(2)}%`;
          }
          if (titleVal.indexOf("||") !== -1) {
              const titleParts = titleVal.split("||");
              const scorePart = titleParts[1] ? titleParts[1].trim() : "";
              if (scorePart.startsWith("Score:")) rawScore = scorePart.replace("Score:", "").trim();
              else if (scorePart) rawScore = scorePart;
              if (rawScore && rawScore !== "N/A" && !String(rawScore).includes("%")) rawScore = `${parseFloat(rawScore).toFixed(2)}%`;
          }
          
          scoreDisplay = <span style={{fontWeight: 700}}>{rawScore === "-" || rawScore === "NaN%" ? "N/A" : rawScore}</span>;

          if (status === "Completed") status = "Success";
          if (isCancelled) {
              status = "Cancelled";
          } else if (status === "Closed - Expired") {
              const scoreVal = t.cr3ea_overall_score ? parseFloat(t.cr3ea_overall_score) : 0;
              if (scoreVal >= 80) status = "Success - Expired";
              else status = "Failed - Expired";
          }

          const isClearedVal = t.cr3ea_islineclear;
          const isCleared = (
              isClearedVal === true || isClearedVal === "true" || isClearedVal === 1 || isClearedVal === "1" || isClearedVal === "Yes" ||
              status === "Completed" || status === "Closed" || status === "Closed - Expired" || status === "Success" || status === "Success - Expired"
          );

          isLineClearBadge = isCleared ? (
            <span style={{ backgroundColor: '#ffffff', color: '#16a34a', border: '1px solid #86efac', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
              <i className="bi bi-check-square" style={{marginRight: '4px'}}></i>Yes
            </span>
          ) : (
            <span style={{ backgroundColor: '#ffffff', color: '#dc2626', border: '1px solid #fca5a5', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
              <i className="bi bi-x-square" style={{marginRight: '4px'}}></i>No
            </span>
          );
      }

      const hasPendingSync = alcPendingSync.some((p: any) =>
        (p.type === 'SESSION_UPDATE' && p.payload.cr3ea_prod_qualitytourid === tourId) ||
        (p.type === 'SESSION_CREATE' && p.payload.cr3ea_prod_qualitytourid === tourId)
      );
      if (hasPendingSync) {
        status = "Pending Sync";
      }

      return {
        id: tourId,
        date,
        form,
        line,
        shift,
        execs,
        score: <span style={{fontWeight: 700}}>{scoreDisplay}</span>,
        isLineClearBadge,
        cycleBadge,
        freqBadge,
        result,
        status,
        pendingWith: "N/A",
        isMyTask: false,
        isEscalatedForMe: false,
        isClickable: !isCancelled,
        rawTour: t
      };
    };

    setOngoingTours(ongoingRaw.map(processTourRow));
    setClosedTours(closedRaw.map(processClosedTourRow));
    setCurrentPage(1);
  };

  const loadDashboardData = async (retryCount = 0) => {
    if (retryCount === 0) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const tokenResult = await getAccessToken();
      const token = tokenResult?.token;
      if (!token) throw new Error("No access token available.");

      const apiVersion = "9.2";
      const baseApiUrl = ENVIRONMENT_URL || '';

      const filterAll = "?$filter=(cr3ea_plantid eq '14' or cr3ea_plantid eq '2' or cr3ea_plantid eq 'Rajpura')&$orderby=cr3ea_tourstartdate desc&$top=100";
      
      const tourTableCandidates = [
        RAJPURA_TABLES.QUALITY_TOUR
      ];

      let list: any[] = [];
      for (const tTable of tourTableCandidates) {
        if (!tTable) continue;
        try {
          const urlAll = `${baseApiUrl}/api/data/v${apiVersion}/${tTable}${filterAll}`;
          const res = await fetch(urlAll, { headers: { "Accept": "application/json", "Authorization": `Bearer ${token}` } });
          if (res.status === 401 && retryCount < 1) {
            console.warn("Token expired. Retrying...");
            localStorage.removeItem("access_token");
            return loadDashboardData(retryCount + 1);
          }
          if (res.ok) {
            const data = await res.json();
            const items = data.value || [];
            items.forEach((item: any) => {
              item._tableName = tTable;
              const id = getRajpuraTourId(item);
              if (id) {
                const exists = list.some((existing: any) => {
                  const exId = getRajpuraTourId(existing);
                  return String(exId).toLowerCase() === String(id).toLowerCase();
                });
                if (!exists) list.push(item);
              } else {
                list.push(item);
              }
            });
            // Stop once the active table responds successfully
            if (res.status === 200) {
              break;
            }
          }
        } catch (tableErr) {
          console.warn(`Could not fetch tours from candidate table '${tTable}':`, tableErr);
        }
      }

      // Merge Food Safety tours from foodSafetyService (Dataverse + IndexedDB + local history)
      try {
        const fsTours = await foodSafetyService.getTourHistory();
        if (fsTours && Array.isArray(fsTours)) {
          fsTours.forEach((ft: any) => {
            const ftId = getRajpuraTourId(ft).toLowerCase();
            if (ftId) {
              const exists = list.some((t: any) => 
                getRajpuraTourId(t).toLowerCase() === ftId
              );
              if (!exists) {
                list.push(ft);
              }
            }
          });
        }
      } catch (fsErr) {
        console.warn("Could not merge Food Safety tours into main dashboard:", fsErr);
      }

      // Merge CCP, OPRP & Sieves tours from ccpOprpSievesService (Dataverse + IndexedDB + local history)
      try {
        const ccpTours = await ccpOprpSievesService.getTourHistory();
        if (ccpTours && Array.isArray(ccpTours)) {
          ccpTours.forEach((ct: any) => {
            const ctId = getRajpuraTourId(ct).toLowerCase();
            if (ctId) {
              const exists = list.some((t: any) => 
                getRajpuraTourId(t).toLowerCase() === ctId
              );
              if (!exists) {
                list.push(ct);
              }
            }
          });
        }
      } catch (ccpErr) {
        console.warn("Could not merge CCP/OPRP/Sieves tours into main dashboard:", ccpErr);
      }

      // Merge Mixing & Baking tours from mixingAndBakingService (Dataverse + IndexedDB + local history)
      try {
        const mbTours = await mixingAndBakingService.getTourHistory();
        if (mbTours && Array.isArray(mbTours)) {
          mbTours.forEach((mt: any) => {
            const mtId = getRajpuraTourId(mt).toLowerCase();
            if (mtId) {
              const exists = list.some((t: any) => 
                getRajpuraTourId(t).toLowerCase() === mtId
              );
              if (!exists) {
                list.push(mt);
              }
            }
          });
        }
      } catch (mbErr) {
        console.warn("Could not merge Mixing & Baking tours into main dashboard:", mbErr);
      }

      list.sort((a: any, b: any) => {
        const valA = a.cr3ea_tourstartdate || a.createdon || "";
        const valB = b.cr3ea_tourstartdate || b.createdon || "";
        const timeA = parseDateMoment(valA);
        const timeB = parseDateMoment(valB);
        return (timeB.isValid() ? timeB.valueOf() : 0) - (timeA.isValid() ? timeA.valueOf() : 0);
      });

      let configs: any[] = [];
      try {
        let spToken = "";
        try {
          const tokenResponse = await instance.acquireTokenSilent({ scopes: [SP_MSAL_SCOPE], account: accounts[0] });
          spToken = tokenResponse.accessToken;
        } catch (e) {
          console.warn("Silent SP token fetch failed.");
        }
        if (spToken) configs = await alcService.getConfig(spToken);
      } catch (e) {
        console.warn("Failed to fetch configs", e);
      }
      setQaConfigList(configs);

      for (const t of list) {
        let status = t.cr3ea_processstatus || t.cr3ea_status || "In Progress";
        if (status !== "Completed" && status !== "Closed" && status !== "Closed - Expired" && status !== "Success") {
          const creationTime = t.cr3ea_tourstartdate || t.createdon;
          if (creationTime) {
            const m = parseDateMoment(creationTime);
            if (m.isValid()) {
              const tourDateLocal = m.local().format("YYYY-MM-DD");
              const todayLocal = moment().format("YYYY-MM-DD");
              if (tourDateLocal !== todayLocal) {
                const tourId = getRajpuraTourId(t);
                if (!tourId) {
                  console.warn("Skipping auto-expire for tour missing tourId:", t);
                  continue;
                }
                const patchTable = t._tableName || (t.cr3ea_prod_rajpura_quality_tourid ? RAJPURA_TABLES.QUALITY_TOUR : RAJPURA_TABLES.DEV_UAT.QUALITY_TOUR);
                console.log(`Auto-expiring tour from previous day (${tourId}) on table '${patchTable}'`);
                try {
                  const res = await fetch(`${baseApiUrl}/api/data/v${apiVersion}/${patchTable}(${tourId})`, {
                    method: "PATCH",
                    headers: { "Accept": "application/json", "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ cr3ea_status: "Closed - Expired", cr3ea_processstatus: "Closed - Expired" })
                  });
                  if (!res.ok && res.status === 404 && patchTable !== RAJPURA_TABLES.DEV_UAT.QUALITY_TOUR) {
                    await fetch(`${baseApiUrl}/api/data/v${apiVersion}/${RAJPURA_TABLES.DEV_UAT.QUALITY_TOUR}(${tourId})`, {
                      method: "PATCH",
                      headers: { "Accept": "application/json", "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                      body: JSON.stringify({ cr3ea_status: "Closed - Expired", cr3ea_processstatus: "Closed - Expired" })
                    });
                  }
                } catch (e) { console.error("Error auto-expiring tour:", e); }
                t.cr3ea_status = "Closed - Expired";
                t.cr3ea_processstatus = "Closed - Expired";
              }
            }
          }
        }
      }

      const toursNeedCheckpoints = list.filter((t: any) => {
        const isFS = t.cr3ea_food_safety_checklisttype || String(t.cr3ea_title || "").includes("FoodSafety_");
        const status = t.cr3ea_processstatus || t.cr3ea_status || "";
        return !isFS && status !== "In Progress" && status !== "Completed" && status !== "Success" && status !== "Closed - Expired";
      });

      if (toursNeedCheckpoints.length > 0) {
        await Promise.all(toursNeedCheckpoints.map(async (t: any) => {
          try {
            const tourId = getRajpuraTourId(t);
            if (tourId) {
              const checkpoints = await alcService.getCheckpoints(tourId);
              t.checkpoints = checkpoints || [];
            }
          } catch (e) { t.checkpoints = []; }
        }));
      }

      // if (list.length === 0) {
      //   // Fallback matching dashboard.js
      //   list = [
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-1", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 2", cr3ea_shiftexecutiveproduction: "John Doe", cr3ea_tourby: "David QA", cr3ea_status: "Pending QA", cr3ea_processstatus: "Pending QA" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-2", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 2", cr3ea_shift: "Shift 1", cr3ea_shiftexecutiveproduction: "Alice Production", cr3ea_tourby: "Emily QA", cr3ea_status: "Failed - Pending Production", cr3ea_processstatus: "Failed - Pending Production" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-3", cr3ea_tourstartdate: new Date(Date.now() - 86400000).toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 3", cr3ea_shiftexecutiveproduction: "John Doe", cr3ea_tourby: "David QA", cr3ea_overall_score: 100, cr3ea_checklist_result: "Pass", cr3ea_status: "Completed", cr3ea_processstatus: "Completed" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-fs-1", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "PPE_Rajpura_Line 1_13082026", cr3ea_food_safety_checklisttype: "PPE Checklist", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 2", cr3ea_shiftexecutiveproduction: "John Prod", cr3ea_assigned_qa: "David QA", cr3ea_status: "In Progress", cr3ea_processstatus: "In Progress", cr3ea_food_safety_cycle: "Cycle-1" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-fs-2", cr3ea_tourstartdate: new Date(Date.now() - 3600000).toISOString(), cr3ea_title: "GMP_Rajpura_Line 2_13082026", cr3ea_food_safety_checklisttype: "GMP Checklist", cr3ea_lineno: "Line 2", cr3ea_shift: "Shift 1", cr3ea_shiftexecutiveproduction: "Alice Incharge", cr3ea_assigned_qa: "David QA", cr3ea_overall_score: "85%", cr3ea_checklist_result: "Pass", cr3ea_status: "Submitted", cr3ea_processstatus: "Submitted", cr3ea_food_safety_cycle: "Cycle-2" }
      //   ];
      // }

      try {
        localStorage.setItem("mrs_bectors_rajpura_dashboard_cache", JSON.stringify(list));
      } catch (_) {}

      setAllToursRaw(list);
      processCategoryTours(list, configs, selectedCategory);

    } catch (err: any) {
      console.warn("Failed to load Dataverse tours live, attempting offline cache:", err);
      let fallbackList: any[] = [];
      try {
        const cached = localStorage.getItem("mrs_bectors_rajpura_dashboard_cache");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            fallbackList = parsed;
          }
        }
      } catch (_) {}

      // if (fallbackList.length === 0) {
      //   fallbackList = [
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-1", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 2", cr3ea_shiftexecutiveproduction: "John Doe", cr3ea_tourby: "David QA", cr3ea_status: "Pending QA", cr3ea_processstatus: "Pending QA" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-2", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 2", cr3ea_shift: "Shift 1", cr3ea_shiftexecutiveproduction: "Alice Production", cr3ea_tourby: "Emily QA", cr3ea_status: "Failed - Pending Production", cr3ea_processstatus: "Failed - Pending Production" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-3", cr3ea_tourstartdate: new Date(Date.now() - 86400000).toISOString(), cr3ea_title: "Area Line Clearance", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 3", cr3ea_shiftexecutiveproduction: "John Doe", cr3ea_tourby: "David QA", cr3ea_overall_score: 100, cr3ea_checklist_result: "Pass", cr3ea_status: "Completed", cr3ea_processstatus: "Completed" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-fs-1", cr3ea_tourstartdate: new Date().toISOString(), cr3ea_title: "PPE_Rajpura_Line 1_13082026", cr3ea_food_safety_checklisttype: "PPE Checklist", cr3ea_lineno: "Line 1", cr3ea_shift: "Shift 2", cr3ea_shiftexecutiveproduction: "John Prod", cr3ea_assigned_qa: "David QA", cr3ea_status: "In Progress", cr3ea_processstatus: "In Progress", cr3ea_food_safety_cycle: "Cycle-1" },
      //     { cr3ea_prod_rajpura_quality_tourid: "mock-fs-2", cr3ea_tourstartdate: new Date(Date.now() - 3600000).toISOString(), cr3ea_title: "GMP_Rajpura_Line 2_13082026", cr3ea_food_safety_checklisttype: "GMP Checklist", cr3ea_lineno: "Line 2", cr3ea_shift: "Shift 1", cr3ea_shiftexecutiveproduction: "Alice Incharge", cr3ea_assigned_qa: "David QA", cr3ea_overall_score: "85%", cr3ea_checklist_result: "Pass", cr3ea_status: "Submitted", cr3ea_processstatus: "Submitted", cr3ea_food_safety_cycle: "Cycle-2" }
      //   ];
      // }
      setAllToursRaw(fallbackList);
      processCategoryTours(fallbackList, qaConfigList, selectedCategory);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRowClick = (tour: TourHistory) => {
    if (tour.status === 'Pending Sync') {
      alert("This tour has offline data pending synchronization. Please sync from the Home Page before continuing.");
      return;
    }

    if (!tour.isClickable) {
      alert("This tour is currently in progress by another QA Executive.");
      return;
    }

    const formLower = tour.form.toLowerCase();
    let path = '';

    if (formLower.includes('alc') || formLower.includes('clearance') || formLower.includes('line')) {
      path = '/rajpura-alc';
    } else if (formLower.includes('ccp') || formLower.includes('oprp') || formLower.includes('sieve')) {
      path = '/oprp-ccp-sieves';
    } else if (formLower.includes('mix') || formLower.includes('baking')) {
      path = '/mixing-baking';
    } else if (formLower.includes('food') || formLower.includes('ppe') || formLower.includes('gmp') || formLower.includes('pci')) {
      path = '/food-safety';
    } else if (
        ["temperatures", "humidity", "code verification", "papa", "pqi", "seal integrity", "cream percentage", "quality wall", "packaging"]
        .some(f => formLower.includes(f))
    ) {
      path = '/rajpura-product-production-operations';
    } else {
      path = '/rajpura-alc';
    }
    navigate(`${path}?id=${tour.id}`);
  };

  const renderStatusBadge = (status: string) => {
    if (status === 'Pending Sync') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#d97706' }}></span>
          Pending Sync
        </span>
      );
    }
    if (status === 'Pending QA' || status === 'QA In Progress' || status === 'In Progress' || status === 'InProgress-paused') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#b45309' }}></span>
          {status}
        </span>
      );
    }
    if (status.includes('Failed') || status === 'Escalated' || status === 'Fail' || status === 'Closed - Expired' || status === 'Cancelled') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
          {status}
        </span>
      );
    }
    if (status.includes('Success') || status === 'Pass' || status === 'Submitted' || status === 'Completed' || status === 'Closed') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
          {status}
        </span>
      );
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading Quality Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-4 text-center">
        <h4>Connection Error</h4>
        <p>{error}</p>
        <button className="btn btn-primary mt-3" onClick={() => loadDashboardData()}>Retry</button>
      </div>
    );
  }

  const totalPages = Math.ceil(closedTours.length / pageSize) || 1;
  const pagedClosedTours = closedTours.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const isFS = selectedCategory === "FoodSafety";
  const isCCP = selectedCategory === "CCP_OPRP_Sieves";

  return (
    <div className="rajpura-dashboard-wrapper" style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '24px', fontFamily: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* Main Container Card matching screenshot */}
      <div className="rajpura-dashboard-card" style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)', padding: '24px' }}>

        {/* Dashboard Top Header Bar */}
        <div className="dashboard-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '20px' }}>
            Rajpura Quality Dashboard
          </h3>

          <div className="dashboard-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', width: '100%', maxWidth: '320px' }}>
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap' }}>Checklist Type:</span>
              <select
                id="dashboardCategorySelect"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as DashboardCategory)}
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#0284c7',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  outline: 'none',
                  flex: 1
                }}
              >
                <option value="ALC">Area Line Clearance (ALC)</option>
                <option value="FoodSafety">Food Safety Checklist</option>
                <option value="CCP_OPRP_Sieves">CCP, OPRP & Sieves</option>
                <option value="MixingAndBaking">Mixing & Baking</option>
                <option value="PackagingOperations">Packaging Operations</option>
              </select>
            </div>

            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#e0f2fe', color: '#0284c7', padding: '6px 14px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600, border: '1px solid #bae6fd', whiteSpace: 'nowrap' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#0284c7' }}></span>
              Rajpura Plant
            </span>
          </div>
        </div>

        {/* KPI Cards Row (4 cards, clean white background, subtle border, colored bold numbers) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, marginBottom: '6px' }}>Total Tours</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{metrics.totalTours}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, marginBottom: '6px' }}>{metrics.metric2Label}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: metrics.metric2Color, lineHeight: 1 }}>{metrics.metric2Value}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, marginBottom: '6px' }}>{metrics.metric3Label}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: metrics.metric3Color, lineHeight: 1 }}>{metrics.metric3Value}</div>
          </div>
          <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, marginBottom: '6px' }}>{metrics.metric4Label}</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: metrics.metric4Color, lineHeight: 1 }}>{metrics.metric4Value}</div>
          </div>
        </div>

        {/* Section 1: Ongoing Clearances & Action Tracking */}
        <div style={{ marginBottom: '36px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '4px', height: '18px', backgroundColor: '#0284c7', borderRadius: '2px' }}></div>
              <h4 style={{ margin: 0, fontWeight: 700, color: '#0284c7', fontSize: '15px' }}>
                Ongoing Clearance Tours & Action Tracking
              </h4>
            </div>

            <button
              id="btn-refresh-dashboard"
              onClick={() => loadDashboardData(1)}
              disabled={refreshing}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                padding: '6px 14px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#0284c7',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              <i className={`bi bi-arrow-clockwise ${refreshing ? 'spinner-border spinner-border-sm' : ''}`}></i>
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>

          <div className="bs-table-container" style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'center' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>DATE & TIME</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isFS ? "CHECKLIST TYPE" : isCCP ? "VERIFICATION TYPE" : "CHECKLIST FORM"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>LINE</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SHIFT</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                    {isFS || isCCP ? "QA / INCHARGE" : "EXECUTIVE DETAILS"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SCORE</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isFS ? "CYCLE" : isCCP ? "FREQUENCY / PRODUCT" : "IS LINE CLEAR"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>STATUS</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>PENDING WITH</th>
                </tr>
              </thead>
              <tbody>
                {ongoingTours.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '36px 16px', color: '#64748b', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500 }}>
                        No ongoing tours found for {selectedCategory === "PackagingOperations" ? "Packaging Operations" : selectedCategory === "MixingAndBaking" ? "Mixing & Baking" : selectedCategory === "FoodSafety" ? "Food Safety" : selectedCategory === "CCP_OPRP_Sieves" ? "CCP, OPRP & Sieves" : "Area Line Clearance"}.
                      </p>
                      {/* <button
                        onClick={() => {
                          if (selectedCategory === "PackagingOperations") navigate("/rajpura-product-production-operations");
                          else if (selectedCategory === "MixingAndBaking") navigate("/rajpura-mixing-and-baking");
                          else if (selectedCategory === "FoodSafety") navigate("/rajpura-food-safety");
                          else if (selectedCategory === "CCP_OPRP_Sieves") navigate("/rajpura-ccp-oprp-sieves");
                          else navigate("/rajpura-alc");
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: '#0284c7',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '7px 16px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        + Start New {selectedCategory === "PackagingOperations" ? "Packaging Operations" : selectedCategory === "MixingAndBaking" ? "Mixing & Baking" : selectedCategory === "FoodSafety" ? "Food Safety" : selectedCategory === "CCP_OPRP_Sieves" ? "CCP/OPRP/Sieves" : "ALC"} Tour
                      </button> */}
                    </td>
                  </tr>
                ) : (
                  ongoingTours.map((tour, idx) => {
                    const rowBg = tour.isEscalatedForMe ? '#fee2e2' : tour.isMyTask ? '#fffbeb' : idx % 2 === 0 ? '#ffffff' : '#fcfdfe';
                    return (
                      <tr
                        key={tour.id || `ongoing-${idx}`}
                        onClick={() => handleRowClick(tour)}
                        style={{
                          backgroundColor: rowBg,
                          borderBottom: '1px solid #e2e8f0',
                          cursor: tour.isClickable ? 'pointer' : 'default',
                          transition: 'background-color 0.1s ease'
                        }}
                        title={tour.isEscalatedForMe ? "CRITICAL: This tour has escalated!" : tour.isClickable ? "Click to open tour" : "In progress by another QA"}
                      >
                        <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.date}</td>
                        <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a' }}>{tour.form}</td>
                        <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.line}</td>
                        <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.shift}</td>
                        <td style={{ padding: '14px 16px', color: '#334155', fontSize: '13px', lineHeight: '1.4' }}>{tour.execs}</td>
                        <td style={{ padding: '14px 16px' }}>{tour.score}</td>
                        <td style={{ padding: '14px 16px' }}>
                          {isFS ? tour.cycleBadge : isCCP ? tour.freqBadge : tour.isLineClearBadge || "-"}
                        </td>
                        <td style={{ padding: '14px 16px' }}>
                          {renderStatusBadge(tour.status)}
                        </td>
                        <td style={{ padding: '14px 16px', fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{tour.pendingWith}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 2: Closed, Completed & Expired Archives */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '4px', height: '18px', backgroundColor: '#16a34a', borderRadius: '2px' }}></div>
              <h4 style={{ margin: 0, fontWeight: 700, color: '#16a34a', fontSize: '15px' }}>
                Closed, Completed & Expired Archives
              </h4>
            </div>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
              {closedTours.length} Total Records
            </span>
          </div>

          <div className="bs-table-container" style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'center' }}>
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>DATE & TIME</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isFS ? "CHECKLIST TYPE" : isCCP ? "VERIFICATION TYPE" : "CHECKLIST FORM"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>LINE</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SHIFT</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                    {isFS || isCCP ? "QA / INCHARGE" : "EXECUTIVE DETAILS"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>SCORE</th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {isFS ? "CYCLE" : isCCP ? "FREQUENCY / PRODUCT" : "IS LINE CLEAR"}
                  </th>
                  <th style={{ padding: '12px 16px', color: '#475569', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>STATUS / RESULT</th>
                </tr>
              </thead>
              <tbody>
                {pagedClosedTours.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '32px 16px', color: '#64748b', textAlign: 'center' }}>
                      No closed/completed tour archives.
                    </td>
                  </tr>
                ) : (
                  pagedClosedTours.map((tour, idx) => (
                    <tr
                      key={tour.id || `closed-${idx}`}
                      onClick={() => handleRowClick(tour)}
                      style={{
                        backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fcfdfe',
                        borderBottom: '1px solid #e2e8f0',
                        cursor: 'pointer',
                        transition: 'background-color 0.1s ease'
                      }}
                      title="Click to view details"
                    >
                      <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.date}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a' }}>{tour.form}</td>
                      <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.line}</td>
                      <td style={{ padding: '14px 16px', color: '#475569' }}>{tour.shift}</td>
                      <td style={{ padding: '14px 16px', color: '#334155', fontSize: '13px', lineHeight: '1.4' }}>{tour.execs}</td>
                      <td style={{ padding: '14px 16px' }}>{tour.score}</td>
                      <td style={{ padding: '14px 16px' }}>
                        {isFS ? tour.cycleBadge : isCCP ? tour.freqBadge : tour.isLineClearBadge || "-"}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {renderStatusBadge(tour.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '16px 0', marginTop: '8px' }}>
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: currentPage === 1 ? '#94a3b8' : '#334155',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: currentPage === totalPages ? '#94a3b8' : '#334155',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default RajpuraQualityDashboard;